import styled from '@emotion/styled';
import { Button, ThemeProps } from '@teko/ui';
import { t } from 'i18n';
import { useState } from 'react';
import {
  SelectOption,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Body,
  Select,
} from '@teko/ds-mobile';
import { stringToInteger } from '@teko/utils';
import { SizeEnum } from '@teko/types/ds';

const StyledModal = styled(Modal)<{ isMobile: boolean }>`
  max-width: 90%;
  ${({ isMobile }) =>
    isMobile
      ? `
    .teko-modal-content {
      padding: 0.25rem;
    }`
      : ''}
`;

const StyledModalBody = styled(ModalBody)`
  padding: 0 1rem;
`;

const StyledModalFooter = styled(ModalFooter)`
  column-gap: 1rem;
`;

const StyledModalFooterDesktop = styled(StyledModalFooter)`
  justify-content: end;
`;

const CancelButton = styled(Button)(
  ({ theme }: ThemeProps) => `
  border: 0.5px solid ${theme?.brand.primary};
  background: #fff;
  color: ${theme?.brand.primary};
`,
);

const StyledTitle = styled.div`
  font-weight: 500;
  font-size: 13px;
  margin-bottom: 4px;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const FormItemWrapper = styled.div`
  margin-bottom: 0.5rem;
`;

interface CheckAgeModalProps {
  show: boolean;
  onCancel: () => void;
  onOk: () => void;
  okText?: string;
}

interface CheckAgeModalContentProps extends CheckAgeModalProps {
  isMobile: boolean;
}

const getCheckYearOptions = () => {
  const options: SelectOption[] = [];
  let startYear = new Date().getFullYear() - 18;

  for (let i = 0; i < 60; i++) {
    const year = startYear--;
    options.push({ text: `${year}`, value: year });
  }

  return options;
};

export const CheckAgeModalContent: React.FC<CheckAgeModalContentProps> = ({
  show,
  onCancel,
  onOk,
  okText = t('Xác nhận'),
  isMobile,
}) => {
  const [name, setName] = useState<string>('');
  const [year, setYear] = useState<number>();

  const onSelectYear = (value: string | number): void => {
    // UX4BUYER-4531
    // make sure the passed value is number
    const year = typeof value === 'number' ? value : stringToInteger(value, 0);
    setYear(year);
  };
  const buttonStyle = isMobile
    ? { width: '50%', display: 'inline-block' }
    : { minWidth: '100px', display: 'inline-block', height: '36px' };
  const Footer = isMobile ? StyledModalFooter : StyledModalFooterDesktop;

  return (
    <StyledModal
      isMobile={isMobile}
      visible={show}
      size={isMobile ? SizeEnum.m : SizeEnum.s}
      showCloseIcon={false}
      footer={false}
    >
      <ModalHeader>
        {t('Xác nhận')}
        <Body color="textSecondary" weight="regular">
          {t(
            'sites.shop-front.components.mobile.templates.ProductDetailTemplate.1',
          )}
        </Body>
      </ModalHeader>

      <StyledModalBody>
        <FormItemWrapper>
          <StyledTitle>
            {t('Họ tên')}{' '}
            <Body color="textSecondary" weight="regular">{`(${t(
              'bắt buộc',
            )})`}</Body>
          </StyledTitle>
          <Input
            type="text"
            placeholder={t('Vui lòng nhập họ tên')}
            value={name}
            onChange={(value) => setName(value)}
          />
        </FormItemWrapper>
        <FormItemWrapper>
          <Select
            title={
              <StyledTitle>
                {t('Năm sinh')}{' '}
                <Body color="textSecondary" weight="regular">{`(${t(
                  'bắt buộc',
                )})`}</Body>
              </StyledTitle>
            }
            options={getCheckYearOptions()}
            value={year}
            selectValue={onSelectYear}
            maxPerView={8}
          />
        </FormItemWrapper>
      </StyledModalBody>
      <Footer>
        <CancelButton
          padding={['s0', 's20']}
          style={buttonStyle}
          onClick={onCancel}
        >
          {t('Tôi dưới 18 tuổi')}
        </CancelButton>
        <Button
          marginLeft="s16"
          padding={['s0', 's20']}
          style={buttonStyle}
          variant="primary"
          onClick={onOk}
          disabled={!name?.trim() || !year}
        >
          {okText}
        </Button>
      </Footer>
    </StyledModal>
  );
};

export const CheckAgeModalMobile: React.FC<CheckAgeModalProps> = ({
  show,
  onCancel,
  onOk,
}) => {
  return (
    <CheckAgeModalContent
      show={show}
      isMobile={true}
      onOk={onOk}
      onCancel={onCancel}
    />
  );
};

export const CheckAgeModalDesktop: React.FC<CheckAgeModalProps> = ({
  show,
  onCancel,
  onOk,
}) => {
  return (
    <CheckAgeModalContent
      show={show}
      isMobile={false}
      onOk={onOk}
      onCancel={onCancel}
    />
  );
};
